export type Span =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string }
  | { kind: "code"; text: string; language?: string }
  | { kind: "link"; text: string; href: string; host: string };

export type Item = { at: number; depth: number; marker: string; spans: Span[] };

export type Block = { at: number } & (
  | { kind: "paragraph"; spans: Span[] }
  | { kind: "strong"; spans: Span[] }
  | { kind: "quote"; spans: Span[] }
  | { kind: "code"; text: string; language?: string }
  | { kind: "list"; ordered: boolean; items: Item[] }
  | { kind: "table"; headers: Span[][]; rows: Span[][][]; align: ("left" | "center" | "right")[] }
);

const A_LINK_IS = /^https?:\/\/[^\s]+$/i;

const OF_A_WORD = /[A-Za-z0-9]/;

function linkAt(text: string, from: number): { span: Span; length: number } | null {
  const close = text.indexOf("]", from + 1);
  if (close < 0 || text[close + 1] !== "(") return null;
  const end = text.indexOf(")", close + 2);
  if (end < 0) return null;
  const shown = text.slice(from + 1, close);
  const href = text.slice(close + 2, end).trim();
  if (shown === "" || !A_LINK_IS.test(href)) return null;
  let host = "";
  try {
    host = new URL(href).host;
  } catch {
    return null;
  }
  if (host === "") return null;
  return { span: { kind: "link", text: shown, href, host }, length: end + 1 - from };
}

function codeAt(text: string, from: number): { span: Span; length: number } | null {
  let ticks = 0;
  while (text[from + ticks] === "`") ticks += 1;
  const fence = "`".repeat(ticks);
  let at = from + ticks;
  while (at < text.length) {
    const found = text.indexOf(fence, at);
    if (found < 0) return null;
    let run = 0;
    while (text[found + run] === "`") run += 1;
    if (run === ticks) {
      const inner = text.slice(from + ticks, found);
      if (inner === "") return null;

      const shown =
        inner.startsWith(" ") && inner.endsWith(" ") && inner.trim() !== "" ? inner.slice(1, -1) : inner;
      return { span: { kind: "code", text: shown }, length: found + ticks - from };
    }
    at = found + run;
  }
  return null;
}

function emphasisAt(text: string, from: number): { span: Span; length: number } | null {
  const mark = text[from];
  if (mark !== "*" && mark !== "_") return null;
  const double = text[from + 1] === mark;
  const fence = double ? mark + mark : mark;
  const after = text[from + fence.length] ?? "";
  if (after === "" || /\s/.test(after)) return null;
  const before = text[from - 1] ?? "";

  if (mark === "_" && OF_A_WORD.test(before)) return null;
  let at = from + fence.length;
  while (at < text.length) {
    const found = text.indexOf(fence, at);
    if (found <= from + fence.length) return null;
    const closes = text[found - 1] ?? "";
    const beyond = text[found + fence.length] ?? "";
    const ok =
      !/\s/.test(closes) &&
      !(mark === "_" && OF_A_WORD.test(beyond)) &&
      !(text[found + fence.length] === mark && !double);
    if (ok) {
      return {
        span: { kind: double ? "strong" : "em", text: text.slice(from + fence.length, found) },
        length: found + fence.length - from,
      };
    }
    at = found + fence.length;
  }
  return null;
}

const A_PATH_IS = /^~?(\/[A-Za-z0-9_.@-]+){2,}\/?/;

function pathAt(text: string, from: number): { span: Span; length: number } | null {
  const before = text[from - 1] ?? "";
  if (before !== "" && !/[\s(]/.test(before)) return null;
  const match = A_PATH_IS.exec(text.slice(from));
  if (!match) return null;
  const shown = match[0].replace(/[.,;:]+$/, "");
  return { span: { kind: "code", text: shown }, length: shown.length };
}

export function spansOf(text: string): Span[] {
  const spans: Span[] = [];
  let plain = "";
  const flush = () => {
    if (plain !== "") spans.push({ kind: "text", text: plain });
    plain = "";
  };
  let i = 0;
  while (i < text.length) {
    const ch = text[i] as string;

    if (ch === "!" && text[i + 1] === "[") {
      const link = linkAt(text, i + 1);
      if (link) {
        plain += text.slice(i, i + 1 + link.length);
        i += 1 + link.length;
        continue;
      }
    }
    const found =
      ch === "`"
        ? codeAt(text, i)
        : ch === "["
          ? linkAt(text, i)
          : ch === "*" || ch === "_"
            ? emphasisAt(text, i)
            : ch === "/" || (ch === "~" && text[i + 1] === "/")
              ? pathAt(text, i)
              : null;
    if (found) {
      flush();
      spans.push(found.span);
      i += found.length;
      continue;
    }
    plain += ch;
    i += 1;
  }
  flush();
  return spans;
}

const FENCE = /^\s{0,3}(```|~~~)([A-Za-z0-9+#._-]*)/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const BULLET = /^(\s*)([-*+])\s+(.*)$/;
const NUMBER = /^(\s*)(\d{1,9})[.)]\s+(.*)$/;

const A_LEVEL_IS = 2;
const AT_MOST_DEEP = 2;

const depthOf = (spaces: string): number =>
  Math.min(AT_MOST_DEEP, Math.floor(spaces.replace(/\t/g, "  ").length / A_LEVEL_IS));

function cellsOf(line: string): string[] | null {
  const cells: string[] = [];
  let cell = "";
  let pipes = 0;
  const source = line.trim();
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    if (ch === "\\" && source[i + 1] === "\\") {
      cell += "\\";
      i += 1;
    } else if (ch === "\\" && source[i + 1] === "|") {
      cell += "|";
      i += 1;
    } else if (ch === "`") {
      const code = codeAt(source, i);
      if (code) {
        cell += source.slice(i, i + code.length).replace(/\\\|/g, "|");
        i += code.length - 1;
      } else cell += ch;
    } else if (ch === "|") {
      cells.push(cell.trim());
      cell = "";
      pipes += 1;
    } else cell += ch;
  }
  cells.push(cell.trim());
  if (source.startsWith("|")) cells.shift();
  if (source.endsWith("|") && cell === "") cells.pop();
  return pipes ? cells : null;
}

export function blocksOf(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let opened = 0;
  const endParagraph = () => {
    if (paragraph.length > 0)
      blocks.push({ at: opened, kind: "paragraph", spans: spansOf(paragraph.join("\n")) });
    paragraph = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] as string;
    const fence = FENCE.exec(line);
    if (fence) {
      endParagraph();
      const from = i;
      const mark = fence[1] as string;
      const held: string[] = [];
      i += 1;
      while (i < lines.length && !(lines[i] as string).trimStart().startsWith(mark)) {
        held.push(lines[i] as string);
        i += 1;
      }

      if (i < lines.length) i += 1;
      blocks.push({ at: from, kind: "code", text: held.join("\n"), language: fence[2] || undefined });
      continue;
    }
    const headers = cellsOf(line);
    const separator = cellsOf(lines[i + 1] ?? "");
    if (
      headers &&
      headers.length > 0 &&
      separator &&
      headers.length === separator.length &&
      separator.every((cell) => /^:?-{3,}:?$/.test(cell))
    ) {
      endParagraph();
      const from = i;
      const rows: Span[][][] = [];
      i += 2;
      while (i < lines.length && !FENCE.test(lines[i] as string)) {
        const cells = cellsOf(lines[i] as string);
        if (!cells) break;
        rows.push(headers.map((_, column) => spansOf(cells[column] ?? "")));
        i += 1;
      }
      blocks.push({
        at: from,
        kind: "table",
        headers: headers.map(spansOf),
        rows,
        align: separator.map((cell) =>
          cell.endsWith(":") ? (cell.startsWith(":") ? "center" : "right") : "left",
        ),
      });
      continue;
    }
    if (line.trim() === "") {
      endParagraph();
      i += 1;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      endParagraph();
      blocks.push({ at: i, kind: "strong", spans: spansOf((heading[2] as string).replace(/\s+#+\s*$/, "")) });
      i += 1;
      continue;
    }
    if (QUOTE.test(line)) {
      endParagraph();
      const from = i;
      const held: string[] = [];
      while (i < lines.length) {
        const quoted = QUOTE.exec(lines[i] as string);
        if (!quoted) break;
        held.push(quoted[1] as string);
        i += 1;
      }
      blocks.push({ at: from, kind: "quote", spans: spansOf(held.join("\n")) });
      continue;
    }
    const first = BULLET.exec(line) ?? NUMBER.exec(line);
    if (first) {
      endParagraph();
      const ordered = NUMBER.test(line);
      const from = i;
      const items: Item[] = [];
      while (i < lines.length) {
        const at = lines[i] as string;
        const one = ordered ? NUMBER.exec(at) : BULLET.exec(at);

        if (!one || (ordered ? BULLET.test(at) : NUMBER.test(at))) break;
        items.push({
          at: i,
          depth: depthOf(one[1] as string),
          marker: ordered ? `${one[2]}.` : "\u2022",
          spans: spansOf(one[3] as string),
        });
        i += 1;
      }
      blocks.push({ at: from, kind: "list", ordered, items });
      continue;
    }
    if (paragraph.length === 0) opened = i;
    paragraph.push(line);
    i += 1;
  }
  endParagraph();
  return blocks;
}

export function isMarked(text: string): boolean {
  const blocks = blocksOf(text);
  if (blocks.length !== 1) return true;
  const only = blocks[0] as Block;
  if (only.kind !== "paragraph") return true;
  return only.spans.some((span) => span.kind !== "text");
}
